import { FC, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'

import { PhoneFieldController } from '../../../components/forms/fields/PhoneFieldController'
import { TextFieldController } from '../../../components/forms/fields/TextFieldController'
import { toast } from '../../../components/LsToast'
import { createCustomer, updateCustomer } from '../../../service/customers.service'
import { CustomerDto } from '../../../service/types'
import { getApiErrorMessage } from '../../../utils/getApiErrorMessage'
import { CustomerFormValues, useSchema } from '../schema'

type DefaultCustomerValues = {
  name?: string
  phone?: string
  email?: string
}

type Props = {
  open: boolean
  onClose: () => void
  onSaved: () => void
  customer?: CustomerDto | null
  defaults?: DefaultCustomerValues
  onCreated?: (customer: CustomerDto) => void
}

export const CustomerDialog: FC<Props> = ({ open, onClose, onSaved, customer, defaults, onCreated }) => {
  const { t } = useTranslation()
  const schema = useSchema()

  const { handleSubmit, control, reset } = useForm<CustomerFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      phone: '',
      email: '',
    },
  })

  useEffect(() => {
    if (customer) {
      reset({
        name: customer.name,
        phone: customer.phone ?? '',
        email: customer.email ?? '',
      })
    } else {
      reset({
        name: defaults?.name ?? '',
        phone: defaults?.phone ?? '',
        email: defaults?.email ?? '',
      })
    }
  }, [customer, open, reset, defaults])

  const onSubmit = async (data: CustomerFormValues) => {
    try {
      const payload = {
        name: data.name,
        phone: data.phone || undefined,
        email: data.email || undefined,
      }
      if (customer) {
        await updateCustomer(customer.id, payload)
        toast.success(t('general.notification.savedSuccessfully'))
      } else {
        const res = await createCustomer(payload)
        onCreated?.(res.data)
        if (res.status === 200) {
          toast.info(t('customers.notification.mergedWithExisting'))
        } else {
          toast.success(t('general.notification.savedSuccessfully'))
        }
      }
      onSaved()
      onClose()
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err) || t('general.notification.unexpectedError'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (reason !== 'backdropClick') onClose()
      }}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          {customer ? t('customers.editCustomer') : t('customers.addCustomer')}
          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="caption">{t('customers.form.name')}</Typography>
              <TextFieldController name="name" control={control} />
            </Box>
            <Box>
              <Typography variant="caption">{t('customers.form.phone')}</Typography>
              <PhoneFieldController name="phone" control={control} />
            </Box>
            <Box>
              <Typography variant="caption">{t('customers.form.email')}</Typography>
              <TextFieldController name="email" control={control} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('general.button.cancel')}</Button>
          <Button type="submit" variant="contained">
            {t('general.button.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
